import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Tag,
  Space,
  Drawer,
  Tabs,
  InputNumber,
  DatePicker,
  Radio,
  message,
  Popconfirm,
  Tooltip,
  Col,
  Row,
  Spin,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  HistoryOutlined,
  DeleteOutlined,
  WarningOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { products } from "../api";
import PageHeader from "../components/common/PageHeader";
import { fmtCurrency, fmtDate } from "../utils/format";
import dayjs from "dayjs";

const TYPE_COLORS = { SALE: "blue", RENT: "purple", BOTH: "green" };
const TYPE_LABELS = { SALE: "Sale Only", RENT: "Rent Only", BOTH: "Sale & Rent" };

export default function Products() {
  const [data, setData] = useState({ content: [], totalElements: 0 });
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [rateDrawer, setRateDrawer] = useState(null);
  const [pendingRateType, setPendingRateType] = useState(null); // "sale" | "rent" | null
  const [saleRates, setSaleRates] = useState([]);
  const [rentRates, setRentRates] = useState([]);
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [form] = Form.useForm();
  const [rateForm] = Form.useForm();

  // ── product usage guard ─────────────────────────────────────────────────────
  // true when the product being edited has sales/rental history — locks productType + delete
  const [inUse, setInUse] = useState(false);

  // ── rent rate edit state ────────────────────────────────────────────────────
  const [editingRate, setEditingRate] = useState(null);   // RentRate row being edited
  const [editRateForm] = Form.useForm();
  const [editRateModal, setEditRateModal] = useState(false);

  // ── sale rate edit state ────────────────────────────────────────────────────
  const [editingSaleRate, setEditingSaleRate] = useState(null);
  const [editSaleRateForm] = Form.useForm();
  const [editSaleRateModal, setEditSaleRateModal] = useState(false);

  // billCount map for BOTH rent and sale rates: { [rateId]: number }
  const [billCounts, setBillCounts] = useState({});
  const [saleBillCounts, setSaleBillCounts] = useState({});
  const [billCountLoading, setBillCountLoading] = useState(false);
  const [usageMap, setUsageMap] = useState({}); // { [productId]: boolean }

  const productType = Form.useWatch("productType", form);

  const load = async () => {
    setLoading(true);
    try {
      const r = await products.list({ q, page, size: 20 });
      setData(r.data.data);
      // Load usage for every product in the page (fire-and-forget)
      const items = r.data.data?.content || [];
      Promise.all(items.map(async (p) => {
        try {
          const res = await products.usage(p.id);
          return [p.id, res.data.data === true];
        } catch { return [p.id, false]; }
      })).then((entries) => setUsageMap(Object.fromEntries(entries)));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, q]);

  const openEdit = async (rec) => {
    setEditing(rec);
    form.setFieldsValue(
      rec
        ? { ...rec, productType: rec.productType || "SALE", lowStockThreshold: rec.lowStockThreshold ?? 0 }
        : { productType: "SALE", lowStockThreshold: 0 }
    );
    if (rec?.id) {
      try {
        const res = await products.usage(rec.id);
        setInUse(res.data.data === true);
      } catch { setInUse(false); }
    } else {
      setInUse(false);
    }
    setModal(true);
  };

  const openRates = async (rec) => {
    setRateDrawer(rec);
    setBillCounts({});
    setSaleBillCounts({});
    // For non-BOTH products, pre-select the type automatically
    if (rec.productType === "SALE") setPendingRateType("sale");
    else if (rec.productType === "RENT") setPendingRateType("rent");
    else setPendingRateType(null); // BOTH: user must choose
    const [sr, rr] = await Promise.all([
      products.getSaleRates(rec.id),
      products.getRentRates(rec.id),
    ]);
    const srData = sr.data.data;
    const rrData = rr.data.data;
    setSaleRates(srData);
    setRentRates(rrData);

    // Load bill counts for all rates in background
    setBillCountLoading(true);
    try {
      const rentCounts = {};
      const saleCounts = {};
      await Promise.all([
        ...rrData.map(async (rate) => {
          try {
            const res = await products.getRentRateBillCount(rec.id, rate.id);
            rentCounts[rate.id] = res.data.data;
          } catch { rentCounts[rate.id] = null; }
        }),
        ...srData.map(async (rate) => {
          try {
            const res = await products.getSaleRateBillCount(rec.id, rate.id);
            saleCounts[rate.id] = res.data.data;
          } catch { saleCounts[rate.id] = null; }
        }),
      ]);
      setBillCounts(rentCounts);
      setSaleBillCounts(saleCounts);
    } finally {
      setBillCountLoading(false);
    }
  };

  const onSave = async (vals) => {
    try {
      if (editing?.id) await products.update(editing.id, vals);
      else await products.create(vals);
      message.success("Saved");
      setModal(false);
      load();
    } catch {
      message.error("Error saving");
    }
  };

  const addRate = async (type) => {
    try {
      const vals = await rateForm.validateFields();
      const payload = { ...vals, fromDate: vals.fromDate.format("YYYY-MM-DD") };
      if (type === "sale") await products.addSaleRate(rateDrawer.id, payload);
      else await products.addRentRate(rateDrawer.id, payload);
      message.success("Rate added");
      rateForm.resetFields();
      setPendingRateType(null);
      openRates(rateDrawer);
    } catch {
      message.error("Error");
    }
  };

  const onDelete = async (id) => {
    try {
      await products.delete(id);
      message.success("Product deleted");
      load();
    } catch (err) {
      const msg = err?.response?.data?.message || "Cannot delete product";
      message.error(msg);
    }
  };

  // ── rent rate edit ─────────────────────────────────────────────────────────
  const openEditRate = (rate) => {
    setEditingRate(rate);
    editRateForm.setFieldsValue({
      rate: rate.rate,
      taxPercentage: rate.taxPercentage,
      defaultAdvance: rate.defaultAdvance,
      fromDate: dayjs(rate.fromDate),
      toDate: rate.toDate ? dayjs(rate.toDate) : null,
    });
    setEditRateModal(true);
  };

  const saveEditRate = async () => {
    try {
      const vals = await editRateForm.validateFields();
      const payload = {
        rate: vals.rate,
        taxPercentage: vals.taxPercentage,
        defaultAdvance: vals.defaultAdvance,
        fromDate: vals.fromDate.format("YYYY-MM-DD"),
        toDate: vals.toDate ? vals.toDate.format("YYYY-MM-DD") : null,
      };
      await products.updateRentRate(rateDrawer.id, editingRate.id, payload);
      message.success("Rate updated");
      setEditRateModal(false);
      setEditingRate(null);
      openRates(rateDrawer);
    } catch (err) {
      const msg = err?.response?.data?.message || "Cannot update rate";
      message.error(msg);
    }
  };

  const deleteRentRate = async (rate) => {
    try {
      await products.deleteRentRate(rateDrawer.id, rate.id);
      message.success("Rate deleted");
      openRates(rateDrawer);
    } catch (err) {
      const msg = err?.response?.data?.message || "Cannot delete rate";
      message.error(msg);
    }
  };

  // ── sale rate edit / delete ────────────────────────────────────────────────
  const openEditSaleRate = (rate) => {
    setEditingSaleRate(rate);
    editSaleRateForm.setFieldsValue({
      rate: rate.rate,
      taxPercentage: rate.taxPercentage,
      fromDate: dayjs(rate.fromDate),
      toDate: rate.toDate ? dayjs(rate.toDate) : null,
    });
    setEditSaleRateModal(true);
  };

  const saveEditSaleRate = async () => {
    try {
      const vals = await editSaleRateForm.validateFields();
      const payload = {
        rate: vals.rate,
        taxPercentage: vals.taxPercentage,
        fromDate: vals.fromDate.format("YYYY-MM-DD"),
        toDate: vals.toDate ? vals.toDate.format("YYYY-MM-DD") : null,
      };
      await products.updateSaleRate(rateDrawer.id, editingSaleRate.id, payload);
      message.success("Sale rate updated");
      setEditSaleRateModal(false);
      setEditingSaleRate(null);
      openRates(rateDrawer);
    } catch (err) {
      const msg = err?.response?.data?.message || "Cannot update sale rate";
      message.error(msg);
    }
  };

  const doDeleteSaleRate = async (rate) => {
    try {
      await products.deleteSaleRate(rateDrawer.id, rate.id);
      message.success("Sale rate deleted");
      openRates(rateDrawer);
    } catch (err) {
      const msg = err?.response?.data?.message || "Cannot delete sale rate";
      message.error(msg);
    }
  };

  // ── columns ────────────────────────────────────────────────────────────────
  const cols = [
    {
      title: "Name", dataIndex: "name", key: "name",
      render: (v) => <span className="mono" style={{ color: "var(--text)" }}>{v}</span>,
    },
    { title: "Unit", dataIndex: "unit", key: "unit", render: (v) => <Tag>{v}</Tag> },
    {
      title: "Type", dataIndex: "productType", key: "productType",
      render: (v) => { const t = v || "SALE"; return <Tag color={TYPE_COLORS[t]}>{TYPE_LABELS[t]}</Tag>; },
    },
    {
      title: "Low Stock Alert", dataIndex: "lowStockThreshold", key: "lowStockThreshold",
      render: (v) => v > 0 ? (
        <Tooltip title={`Alert when stock ≤ ${v}`}>
          <Tag icon={<WarningOutlined />} color="orange">≤ {v}</Tag>
        </Tooltip>
      ) : <span style={{ color: "var(--text-muted)" }}>—</span>,
    },
    {
      title: "Actions", key: "a",
      render: (_, r) => {
        const used = usageMap[r.id] === true;
        return (
          <Space>
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>Edit</Button>
            <Button size="small" icon={<HistoryOutlined />} onClick={() => openRates(r)}>Rates</Button>
            <Tooltip title={used ? "Cannot delete — this product has been used in sales or rentals" : "Delete this product"}>
              <span>
                <Popconfirm
                  title="Delete this Product?"
                  description="This cannot be undone."
                  onConfirm={() => onDelete(r.id)}
                  okText="Delete" okButtonProps={{ danger: true }} cancelText="Cancel"
                  disabled={used}
                >
                  <Button size="small" icon={used ? <LockOutlined /> : <DeleteOutlined />} danger={!used} disabled={used}>
                    Delete
                  </Button>
                </Popconfirm>
              </span>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  const saleCols = [
    { title: "Rate", dataIndex: "rate", render: (v) => <span className="mono">{fmtCurrency(v)}</span> },
    { title: "Tax %", dataIndex: "taxPercentage", render: (v) => <span className="mono">{v}%</span> },
    { title: "From", dataIndex: "fromDate", render: fmtDate },
    {
      title: "To", dataIndex: "toDate",
      render: (v) => v ? fmtDate(v) : <Tag color="green">Active</Tag>,
    },
    {
      title: "Actions", key: "actions",
      render: (_, rate) => {
        const count = saleBillCounts[rate.id];
        const isLoading = billCountLoading && count === undefined;
        const hasSales  = count === null || (count !== undefined && count > 0);
        const hasToDate = rate.toDate != null;

        const editDisabled   = isLoading || hasSales;
        const deleteDisabled = isLoading || hasSales || hasToDate;

        const editTooltip = isLoading
          ? "Checking sales…"
          : hasSales
            ? `${count} sale(s) used this rate — cannot edit historical rates`
            : "Edit this rate";

        const deleteTooltip = isLoading
          ? "Checking sales…"
          : hasSales
            ? `${count} sale(s) used this rate — delete those sales first`
            : hasToDate
              ? "Cannot delete a closed rate (has a To date)"
              : "Delete this rate";

        return (
          <Space>
            <Tooltip title={editTooltip}>
              <Button
                size="small"
                icon={editDisabled ? <LockOutlined /> : <EditOutlined />}
                disabled={editDisabled}
                onClick={() => openEditSaleRate(rate)}
              >
                Edit
              </Button>
            </Tooltip>
            <Tooltip title={deleteTooltip}>
              <span>
                <Popconfirm
                  title="Delete this sale rate?"
                  description="This cannot be undone."
                  onConfirm={() => doDeleteSaleRate(rate)}
                  okText="Delete" okButtonProps={{ danger: true }} cancelText="Cancel"
                  disabled={deleteDisabled}
                >
                  <Button
                    size="small"
                    icon={deleteDisabled ? <LockOutlined /> : <DeleteOutlined />}
                    danger={!deleteDisabled}
                    disabled={deleteDisabled}
                  >
                    Delete
                  </Button>
                </Popconfirm>
              </span>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  const rentCols = [
    { title: "Rate", dataIndex: "rate", render: (v) => <span className="mono">{fmtCurrency(v)}</span> },
    { title: "Tax %", dataIndex: "taxPercentage", render: (v) => <span className="mono">{v}%</span> },
    {
      title: "Advance/Unit", dataIndex: "defaultAdvance",
      render: (v) => v > 0
        ? <span className="mono" style={{ color: "var(--warning, #d46b08)" }}>₹{Number(v).toFixed(2)}</span>
        : <span style={{ color: "var(--text-muted, #aaa)" }}>—</span>,
    },
    { title: "From", dataIndex: "fromDate", render: fmtDate },
    {
      title: "To", dataIndex: "toDate",
      render: (v) => v ? fmtDate(v) : <Tag color="green">Active</Tag>,
    },
    {
      title: "Actions", key: "actions",
      render: (_, rate) => {
        const count = billCounts[rate.id];           // undefined = still loading, null = fetch error
        const isLoading = billCountLoading && count === undefined;
        const hasBills = count === null || (count !== undefined && count > 0);
        const hasToDate = rate.toDate != null;       // closed period (both from & to set)

        // Edit: blocked only when bills are CONFIRMED to exist
        const editDisabled = isLoading || hasBills;

        // Delete: blocked when bills exist OR when toDate is set (closed/historical period)
        const deleteDisabled = isLoading || hasBills || hasToDate;

        const editTooltip = isLoading
          ? "Checking bills…"
          : hasBills
            ? `${count} bill(s) exist for this period — delete those bills first`
            : "Edit this rate";

        const deleteTooltip = isLoading
          ? "Checking bills…"
          : hasBills
            ? `${count} bill(s) exist for this period — delete those bills first`
            : hasToDate
              ? "Cannot delete a closed rate (has both From and To date)"
              : "Delete this rate";

        return (
          <Space>
            <Tooltip title={editTooltip}>
              <Button
                size="small"
                icon={editDisabled ? <LockOutlined /> : <EditOutlined />}
                disabled={editDisabled}
                onClick={() => openEditRate(rate)}
              >
                Edit
              </Button>
            </Tooltip>

            <Tooltip title={deleteTooltip}>
              {/* Wrap in span so Tooltip works on a disabled button */}
              <span>
                <Popconfirm
                  title="Delete this rate?"
                  description="This action cannot be undone."
                  onConfirm={() => deleteRentRate(rate)}
                  okText="Delete" okButtonProps={{ danger: true }} cancelText="Cancel"
                  disabled={deleteDisabled}
                >
                  <Button
                    size="small"
                    icon={deleteDisabled ? <LockOutlined /> : <DeleteOutlined />}
                    danger={!deleteDisabled}
                    disabled={deleteDisabled}
                  >
                    Delete
                  </Button>
                </Popconfirm>
              </span>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  // Which rate tabs to show depends on product type
  const rateTabItems = [];
  const rt = rateDrawer?.productType || "SALE";

  if (rt === "SALE" || rt === "BOTH") {
    rateTabItems.push({
      key: "sale",
      label: "Sale Rates",
      children: (
        <Spin spinning={billCountLoading} tip="Loading sale counts…">
          <Table
            scroll={{ x: "max-content" }}
            columns={saleCols}
            dataSource={saleRates}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 10, showSizeChanger: false, size: "small" }}
          />
        </Spin>
      ),
    });
  }

  if (rt === "RENT" || rt === "BOTH") {
    rateTabItems.push({
      key: "rent",
      label: "Rent Rates",
      children: (
        <Spin spinning={billCountLoading} tip="Loading bill counts…">
          <Table
            scroll={{ x: "max-content" }}
            columns={rentCols}
            dataSource={rentRates}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 10, showSizeChanger: false, size: "small" }}
          />
        </Spin>
      ),
    });
  }

  return (
    <div>
      <PageHeader
        title="Products"
        subtitle="Manage product catalog and rate history"
        actions={[
          <Input.Search
            key="s"
            placeholder="Search..."
            style={{ width: 220 }}
            onSearch={setQ}
            allowClear
          />,
          <Button key="a" type="primary" icon={<PlusOutlined />} onClick={() => openEdit(null)}>
            Add Product
          </Button>,
        ]}
      />

      <Table
        columns={cols}
        scroll={{ x: "max-content" }}
        dataSource={data.content}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{
          total: data.totalElements,
          pageSize: 20,
          current: page + 1,
          onChange: (p) => setPage(p - 1),
        }}
      />

      {/* Add / Edit Product Modal */}
      <Modal open={modal} title={editing?.id ? "Edit Product" : "New Product"} onCancel={() => setModal(false)} footer={null}>
        <Form form={form} layout="vertical" onFinish={onSave}>
          <Form.Item name="name" label="Product Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="unit" label="Unit" rules={[{ required: true }]}>
            <Input placeholder="pcs / kg / m" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item
            name="lowStockThreshold"
            label="Low Stock Alert Threshold"
            tooltip="Show a warning when available stock falls to or below this number. Set 0 to disable."
          >
            <InputNumber min={0} placeholder="0 = disabled" style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="productType" label="Product is for" rules={[{ required: true, message: "Please select a product type" }]}>
            <Radio.Group buttonStyle="solid" disabled={inUse}>
              <Radio.Button value="SALE">Sale Only</Radio.Button>
              <Radio.Button value="RENT">Rent Only</Radio.Button>
              <Radio.Button value="BOTH">Sale &amp; Rent</Radio.Button>
            </Radio.Group>
            {inUse && (
              <div style={{ marginTop: 6, fontSize: 12, color: "var(--warning, #d46b08)", display: "flex", alignItems: "center", gap: 6 }}>
                <LockOutlined />
                Type is locked — this product has been used in sales or rentals.
              </div>
            )}
          </Form.Item>
          <Button type="primary" htmlType="submit" block>Save</Button>
        </Form>
      </Modal>

      {/* Rates Drawer */}
      <Drawer
        title={`Rates: ${rateDrawer?.name}`}
        open={!!rateDrawer}
        onClose={() => { setRateDrawer(null); setBillCounts({}); setPendingRateType(null); }}
        width={720}
      >
        {/* Add new rate form */}
        <Form form={rateForm} layout="inline" style={{ marginBottom: 16 }}>
          <Row gutter={[0, 12]}>
            {rt === "BOTH" && (
              <Col span={24}>
                <Form.Item label="Adding rate for">
                  <Radio.Group
                    value={pendingRateType}
                    onChange={(e) => { setPendingRateType(e.target.value); rateForm.resetFields(["fromDate"]); }}
                    buttonStyle="solid"
                    size="small"
                  >
                    <Radio.Button value="sale">Sale</Radio.Button>
                    <Radio.Button value="rent">Rent</Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>
            )}
            <Col span={6}>
              <Form.Item name="rate" rules={[{ required: true }]}>
                <InputNumber placeholder="Rate" prefix="₹" min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="taxPercentage">
                <InputNumber placeholder="Tax %" min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            {(pendingRateType === "rent" || rt === "RENT") && (
              <Col span={6}>
                <Form.Item name="defaultAdvance" tooltip="Default advance collected per unit when renting at this rate">
                  <InputNumber placeholder="Advance/unit ₹" prefix="₹" min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            )}
            <Col span={6}>
              <Form.Item name="fromDate" rules={[{ required: true }]}>
                <DatePicker
                  placeholder="From Date"
                  style={{ width: "100%" }}
                  disabledDate={(current) => {
                    const relevantRates = pendingRateType === "sale" ? saleRates
                      : pendingRateType === "rent" ? rentRates
                      : [...saleRates, ...rentRates];
                    if (relevantRates.length === 0) return false;
                    const latestFrom = relevantRates.reduce((max, r) =>
                      dayjs(r.fromDate).isAfter(max) ? dayjs(r.fromDate) : max,
                      dayjs(relevantRates[0].fromDate)
                    );
                    return current && current.isBefore(latestFrom.add(1, "day"), "day");
                  }}
                />
              </Form.Item>
            </Col>
            <Space>
              {(rt === "SALE" || (rt === "BOTH" && pendingRateType === "sale")) && (
                <Col span={6}>
                  <Button type="primary" size="small" onClick={() => addRate("sale")}>+ Sale Rate</Button>
                </Col>
              )}
              {(rt === "RENT" || (rt === "BOTH" && pendingRateType === "rent")) && (
                <Col span={6}>
                  <Button size="small" onClick={() => addRate("rent")}>+ Rent Rate</Button>
                </Col>
              )}
            </Space>
          </Row>
        </Form>

        <Tabs items={rateTabItems} />
      </Drawer>

      {/* Edit Rent Rate Modal */}
      <Modal
        open={editRateModal}
        title="Edit Rent Rate"
        onCancel={() => { setEditRateModal(false); setEditingRate(null); }}
        onOk={saveEditRate}
        okText="Save Changes"
      >
        <Form form={editRateForm} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="rate" label="Rate (₹)" rules={[{ required: true }]}>
                <InputNumber prefix="₹" min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="taxPercentage" label="Tax %">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="defaultAdvance" label="Default Advance / Unit (₹)">
                <InputNumber prefix="₹" min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="fromDate" label="From Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="toDate" label="To Date">
                <DatePicker style={{ width: "100%" }} placeholder="Leave blank if active" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Edit Sale Rate Modal */}
      <Modal
        open={editSaleRateModal}
        title="Edit Sale Rate"
        onCancel={() => { setEditSaleRateModal(false); setEditingSaleRate(null); }}
        onOk={saveEditSaleRate}
        okText="Save Changes"
      >
        <Form form={editSaleRateForm} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="rate" label="Rate (₹)" rules={[{ required: true }]}>
                <InputNumber prefix="₹" min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="taxPercentage" label="Tax %">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="fromDate" label="From Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="toDate" label="To Date">
                <DatePicker style={{ width: "100%" }} placeholder="Leave blank if active" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
}